import React, { useState, useMemo } from 'react';
import { cleanSongInfo, findDuplicates, previewCleanup, DuplicateResult } from '../../utils/songUtils';
import { Button } from '../shared/Button';
import './SongTools.css';

interface SongForTools {
  id: string;
  title: string;
  artist: string;
  year: number;
}

interface SongToolsProps {
  songs: SongForTools[];
  onUpdateSong?: (songId: string, updates: { title?: string; artist?: string }) => void;
  onRemoveSong?: (songId: string) => void;
}

type ActiveTool = 'none' | 'cleanup' | 'duplicates';

export const SongTools: React.FC<SongToolsProps> = ({ songs, onUpdateSong, onRemoveSong }) => {
  const [activeTool, setActiveTool] = useState<ActiveTool>('none');
  const [selectedForCleanup, setSelectedForCleanup] = useState<Set<string>>(new Set());
  const [cleanupApplied, setCleanupApplied] = useState<Set<string>>(new Set());

  // Calculate cleanup previews
  const cleanupPreviews = useMemo(() => {
    return songs.map(song => ({
      ...song,
      preview: previewCleanup(song.title, song.artist)
    })).filter(s => s.preview.titleChanged || s.preview.artistChanged);
  }, [songs]);

  // Find duplicates
  const duplicates = useMemo(() => {
    return findDuplicates(songs);
  }, [songs]);

  // Get song by id
  const getSongById = (id: string) => songs.find(s => s.id === id);

  // Toggle selection for cleanup
  const toggleCleanupSelection = (songId: string) => {
    const newSet = new Set(selectedForCleanup);
    if (newSet.has(songId)) {
      newSet.delete(songId);
    } else {
      newSet.add(songId);
    }
    setSelectedForCleanup(newSet);
  };

  // Select all for cleanup
  const selectAllForCleanup = () => {
    const allIds = cleanupPreviews.map(s => s.id);
    setSelectedForCleanup(new Set(allIds));
  };

  // Deselect all
  const deselectAllForCleanup = () => {
    setSelectedForCleanup(new Set());
  };

  // Apply cleanup to selected songs
  const applyCleanup = () => {
    if (!onUpdateSong) return;

    Array.from(selectedForCleanup).forEach(songId => {
      const song = songs.find(s => s.id === songId);
      if (song) {
        const cleaned = cleanSongInfo(song.title, song.artist);
        onUpdateSong(songId, {
          title: cleaned.title,
          artist: cleaned.artist
        });
        setCleanupApplied(prev => {
          const newSet = new Set(Array.from(prev));
          newSet.add(songId);
          return newSet;
        });
      }
    });

    setSelectedForCleanup(new Set());
  };

  // Remove duplicate
  const removeDuplicate = (songId: string) => {
    if (onRemoveSong) {
      onRemoveSong(songId);
    }
  };

  return (
    <div className="song-tools">
      <div className="tools-header">
        <h3>🛠️ Beta Tools</h3>
        <div className="tools-buttons">
          <button
            className={`tool-btn ${activeTool === 'cleanup' ? 'active' : ''}`}
            onClick={() => setActiveTool(activeTool === 'cleanup' ? 'none' : 'cleanup')}
          >
            🧹 Opschonen ({cleanupPreviews.length})
          </button>
          <button
            className={`tool-btn ${activeTool === 'duplicates' ? 'active' : ''}`}
            onClick={() => setActiveTool(activeTool === 'duplicates' ? 'none' : 'duplicates')}
          >
            👯 Duplicaten ({duplicates.length})
          </button>
        </div>
      </div>

      {/* Cleanup Tool */}
      {activeTool === 'cleanup' && (
        <div className="tool-panel cleanup-panel">
          <div className="tool-description">
            <p>
              Verwijdert automatisch tekst zoals "Official Video", "Remastered 2021", 
              "VEVO", etc. van titels en artiesten.
            </p>
          </div>

          {cleanupPreviews.length === 0 ? (
            <div className="empty-state">
              <p>✨ Alle nummers zijn al schoon!</p>
            </div>
          ) : (
            <>
              <div className="tool-actions">
                <button className="action-btn" onClick={selectAllForCleanup}>
                  Alles selecteren
                </button>
                <button className="action-btn" onClick={deselectAllForCleanup}>
                  Niets selecteren
                </button>
                <Button
                  onClick={applyCleanup}
                  disabled={selectedForCleanup.size === 0 || !onUpdateSong}
                >
                  Pas toe ({selectedForCleanup.size})
                </Button>
              </div>

              <div className="cleanup-list">
                {cleanupPreviews.map(item => (
                  <div 
                    key={item.id} 
                    className={`cleanup-item ${selectedForCleanup.has(item.id) ? 'selected' : ''} ${cleanupApplied.has(item.id) ? 'applied' : ''}`}
                    onClick={() => toggleCleanupSelection(item.id)}
                  >
                    <div className="cleanup-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedForCleanup.has(item.id)}
                        onChange={() => toggleCleanupSelection(item.id)}
                      />
                    </div>
                    <div className="cleanup-preview">
                      {item.preview.titleChanged && (
                        <div className="preview-row">
                          <span className="label">Titel:</span>
                          <span className="original">{item.preview.originalTitle}</span>
                          <span className="arrow">→</span>
                          <span className="cleaned">{item.preview.cleanedTitle}</span>
                        </div>
                      )}
                      {item.preview.artistChanged && (
                        <div className="preview-row">
                          <span className="label">Artiest:</span>
                          <span className="original">{item.preview.originalArtist}</span>
                          <span className="arrow">→</span>
                          <span className="cleaned">{item.preview.cleanedArtist}</span>
                        </div>
                      )}
                    </div>
                    {cleanupApplied.has(item.id) && (
                      <span className="applied-badge">✓ Toegepast</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Duplicates Tool */}
      {activeTool === 'duplicates' && (
        <div className="tool-panel duplicates-panel">
          <div className="tool-description">
            <p>
              Vindt dubbele nummers op basis van titel en artiest vergelijking.
            </p>
          </div>

          {duplicates.length === 0 ? (
            <div className="empty-state">
              <p>✨ Geen duplicaten gevonden!</p>
            </div>
          ) : (
            <div className="duplicates-list">
              {duplicates.map((dup, index) => {
                const duplicateSong = getSongById(dup.songId);
                const originalSong = getSongById(dup.duplicateOf);
                
                if (!duplicateSong || !originalSong) return null;

                return (
                  <div key={index} className={`duplicate-item ${dup.type}`}>
                    <div className="duplicate-info">
                      <div className="duplicate-type">
                        {dup.type === 'exact' ? '🔴 Exacte match' : '🟡 Vergelijkbaar'}
                        <span className="similarity">{Math.round(dup.similarity * 100)}%</span>
                      </div>
                      <div className="duplicate-songs">
                        <div className="song-row original">
                          <span className="song-label">Origineel:</span>
                          <span className="song-details">
                            {originalSong.artist} - {originalSong.title} ({originalSong.year})
                          </span>
                        </div>
                        <div className="song-row duplicate">
                          <span className="song-label">Duplicaat:</span>
                          <span className="song-details">
                            {duplicateSong.artist} - {duplicateSong.title} ({duplicateSong.year})
                          </span>
                        </div>
                      </div>
                    </div>
                    {onRemoveSong && (
                      <button
                        className="remove-duplicate-btn"
                        onClick={() => removeDuplicate(dup.songId)}
                        title="Verwijder duplicaat"
                      >
                        🗑️ Verwijder
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
